<template>
  <InputSchool
    :icon="props.icon"
    :initial-query="props.initialQuery"
    label="School"
    :loading="loading"
    :name="props.name"
    required
    :placeholder="props.placeholder"
    :schools="schoolOptions"
    type="large"
    @search="onSearch"
    @close="onClose"
  />
</template>

<script setup lang="ts">
import type { PaginatedDocs } from 'payload'
import {
  GetSaSchoolsSortedDocument,
  GetSchoolsSortedDocument,
} from '#graphql-exports'

import type { School } from '#payload-types'

interface InputSchoolSearchProps {
  name: string
  placeholder?: string
  icon?: 'school' | 'search'
  /** Pre-runs the search, so the options are already loaded when the box opens. */
  initialQuery?: string
  /** Restricts the results to South Australian schools, for the /sa pages. */
  saOnly?: boolean
}

const props = withDefaults(defineProps<InputSchoolSearchProps>(), {
  placeholder: 'Search by school',
  icon: 'school',
})

interface SchoolsSortedQuery {
  SchoolsSorted: PaginatedDocs<School>
}

const query = ref(props.initialQuery ?? '')

watch(
  () => props.initialQuery,
  (initialQuery) => {
    if (initialQuery) query.value = initialQuery
  },
)
// A separate document rather than a `$state` variable: leaving the variable out would send
// `location__state: {}`, which Payload does not treat as "no filter".
const document = props.saOnly
  ? GetSaSchoolsSortedDocument
  : GetSchoolsSortedDocument

const {
  result,
  loading,
  query: schoolsQuery,
} = useQuery<SchoolsSortedQuery, { query: string; limit: number }>(
  document,
  () => ({
    query: query.value,
    limit: 25,
  }),
  () => ({
    debounce: 250,
    fetchPolicy: 'no-cache',
  }),
)

const schoolOptions = computed(() => {
  return result.value?.SchoolsSorted.docs || []
})

/**
 * Search tracking: one `school_search` per distinct term once its results are
 * in, `school_search_select` on a pick, and `school_search_abandon` when the
 * box closes on a typed term with nothing picked. vue-select clears its search
 * text before it closes (and before a pick lands in the field), so the last
 * typed term is kept aside for those two events.
 *
 * The request is debounced, so `query` can already hold the next term while
 * the previous one's response is still on its way; the term a result belongs
 * to is therefore read off the query that produced it, and counts are kept
 * per term.
 */
const MIN_TRACKED_TERM = 2
const selected = useFieldValue<School | null>(() => props.name)
const resultCounts = new Map<string, number>()
const abandonedTerms = new Set<string>()
let lastTerm = ''

const onSearch = (search: string) => {
  query.value = search
  if (search) lastTerm = search
}

watch(result, () => {
  const term = schoolsQuery.value?.variables?.query ?? ''
  if (term.length < MIN_TRACKED_TERM || resultCounts.has(term)) return

  const count = schoolOptions.value.length
  resultCounts.set(term, count)
  trackEvent('school_search', {
    search_term: term,
    result_count: count,
    sa_only: !!props.saOnly,
  })
})

watch(selected, (school) => {
  if (school) {
    trackEvent('school_search_select', {
      school_slug: school.slug,
      school_name: school.name,
      search_term: lastTerm,
    })
  }

  // A term only counts as abandoned if it was typed after the last pick or
  // clear, not carried over from before.
  lastTerm = ''
})

const onClose = () => {
  if (
    selected.value ||
    lastTerm.length < MIN_TRACKED_TERM ||
    abandonedTerms.has(lastTerm)
  ) {
    return
  }

  abandonedTerms.add(lastTerm)
  trackEvent('school_search_abandon', {
    search_term: lastTerm,
    result_count: resultCounts.get(lastTerm),
    sa_only: !!props.saOnly,
  })
}
</script>
